import { useCallback, useEffect, useRef, useState, FC, ReactNode } from 'react';
import { Box, BoxProps, Tooltip, useTheme } from '@mui/material';

interface OverflowTooltipProps extends Omit<BoxProps, 'title'> {
    children: ReactNode;
    title: ReactNode | string;
    maxLines?: number;
    propToRerender?: any;
}

const OverflowTooltip: FC<OverflowTooltipProps> = ({ children, title, maxLines = 1, propToRerender, sx, ...props }) => {
    const theme = useTheme();
    const ref = useRef(null);
    const [isOverflown, setIsOverflown] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const checkForOverflow = useCallback(() => {
        const element: any = ref.current;
        if (element) {
            setIsOverflown(element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight);
        }
    }, []);

    useEffect(() => {
        checkForOverflow();
    }, [checkForOverflow, propToRerender, title]);

    useEffect(() => {
        window.addEventListener('resize', checkForOverflow);

        return () => {
            window.removeEventListener('resize', checkForOverflow);
        };
    }, [checkForOverflow]);

    const onMouseEnter = () => {
        setIsHovered(true);
    };

    const onMouseLeave = () => {
        setIsHovered(false);
    };

    return (
        <Tooltip open={isOverflown && isHovered} title={title}>
            <Box
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                ref={ref}
                sx={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: maxLines, // Поддержка для многострочного текста
                    wordWrap: 'break-word', // Текст переносится по словам
                    textOverflow: 'ellipsis',
                    WebkitBoxOrient: 'vertical',
                    whiteSpace: maxLines === 1 ? 'nowrap' : 'inherit',
                    fontSize: theme.typography.pxToRem(16),
                    ...sx,
                }}
                {...props}
            >
                {children}
            </Box>
        </Tooltip>
    );
};

export default OverflowTooltip;
